<template>
  <div id="customer-finder">
    <!-- COMPACT TOOLBAR -->
    <div class="vx-row">
      <div class="vx-col w-full">
        <vx-card class="mb-4 cf-toolbar-card">
          <div class="cf-toolbar">
            <div class="cf-title">
              <h4>Customer Finder <span class="cf-help" title="How it works" @click="helpPopup = true">?</span></h4>
              <span class="cf-sub">Who really buys wood &amp; by-products around {{ centerCity || 'the map point' }} — seen in the transport records</span>
            </div>
            <div class="cf-summary">
              <span class="cf-tag">📍 {{ centerCity || (center.lat.toFixed(2) + ',' + center.lng.toFixed(2)) }}</span>
              <span class="cf-tag">⭕ {{ radiusKm }} km</span>
              <span class="cf-tag">📅 {{ periodLabel }}</span>
              <span class="cf-tag">🌳 {{ speciesSummary }}</span>
              <span class="cf-tag prod">📦 {{ productSummary }}</span>
            </div>
            <div class="cf-toolbar-actions">
              <vs-button color="primary" icon-pack="feather" icon="icon-sliders" @click="openFilters">Filters</vs-button>
              <vs-button color="success" type="border" icon-pack="feather" icon="icon-download" @click="exportCSV" :disabled="!tabRows.length">CSV {{ rightTab }}</vs-button>
            </div>
          </div>
          <div class="cf-stats">
            <div class="cf-stat"><div class="n">{{ buyers.length }}</div><div class="l">Real buyers</div></div>
            <div class="cf-stat"><div class="n">{{ seenCount }}</div><div class="l">Seen loading</div></div>
            <div class="cf-stat"><div class="n">{{ transporters.length }}</div><div class="l">Transporters</div></div>
            <div class="cf-stat"><div class="n">{{ supplyTotal }}</div><div class="l">Volume {{ periodLabel }} (m³)</div></div>
            <div class="cf-stat"><div class="n">{{ supply.length }}</div><div class="l">Nearby producers</div></div>
          </div>
        </vx-card>
      </div>
    </div>

    <!-- MAP + LIST -->
    <div class="vx-row">
      <div class="vx-col lg:w-2/3 w-full">
        <vx-card class="mb-4">
          <div class="cf-map-wrap">
            <div v-if="loadingSupply" class="cf-overlay"><vs-progress indeterminate color="primary" style="width:60%"></vs-progress><span>Updating…</span></div>
            <div class="cf-hint">💡 Click on the map to move the starting point</div>
            <l-map ref="cfmap" :zoom="mapZoom" :center="mapCenter" :options="{ preferCanvas: true }" @click="onMapClick" style="height:100%;width:100%">
              <l-tile-layer v-if="useRaster" :url="tileUrl" :attribution="tileAttr"></l-tile-layer>
              <l-circle :lat-lng="[center.lat, center.lng]" :radius="radiusKm*1000" color="#2f9e44" :weight="1.5" fill-color="#2f9e44" :fill-opacity="0.05"></l-circle>
              <l-polyline v-for="ln in buyerLines" :key="ln.key" :lat-lngs="[ln.from, ln.to]"
                :color="ln.color" :weight="ln.weight" :opacity="0.5"></l-polyline>
              <l-circle-marker v-for="(s, i) in supplyMarkers" :key="'s'+i" :lat-lng="[s.lat, s.lng]"
                :radius="Math.max(3, Math.min(9, 3+Math.sqrt(s.sumvol||0)/4))" color="#7a3b00" :weight="1" fill-color="#f0883e" :fill-opacity="0.55">
                <l-popup><b>{{ s.emittant }}</b><br/>Supply: {{ s.sumvol }} m³ ({{ s.n }} transports)<br/>{{ s.species }}<br/>
                  <a href="#" @click.prevent="openTrucks(s)">🚚 Trucks</a></l-popup>
              </l-circle-marker>
              <l-circle-marker v-for="b in mappableBuyers" :key="'b' + b.key" :lat-lng="[b.lat, b.lng]"
                :radius="Math.min(14, 7 + Math.sqrt(b.loads || 1))" color="#0b1622" :weight="2"  fill-color="#3b82f6" :fill-opacity="0.85">
                <l-popup>
                  <b>{{ b.name }}</b> · seen loading<br/>
                  <span v-if="b.city"><span class="cf-k">Location:</span> {{ b.city }}<span v-if="b.county">, {{ b.county }}</span><br/></span>
                  <span v-if="b.caen"><span class="cf-k">CAEN:</span> {{ b.caen }}<br/></span>
                  <span v-if="b.loads"><span class="cf-k">Loads seen:</span> {{ b.loads }} · {{ b.m3 }} m³<br/></span>
                  <span v-if="b.phone"><span class="cf-k">Phone:</span> <a :href="'tel:' + b.phone">{{ b.phone }}</a><br/></span>
                  <span v-if="b.website"><a :href="siteHref(b.website)" target="_blank">website</a><br/></span>
                  <span v-if="b.cui" class="cf-k">CUI {{ b.cui }}</span>
                </l-popup>
              </l-circle-marker>
              <template v-if="rightTab === 'estimated'">
                <l-polyline v-for="ln in estimatedLines" :key="ln.key" :lat-lngs="[ln.from, ln.to]" color="#f59f00" :weight="2" :opacity="0.6" :dash-array="'6,6'"></l-polyline>
                <l-circle-marker v-for="e in mappableEstimated" :key="'e' + e.key" :lat-lng="[e.lat, e.lng]" :radius="8" color="#f59f00" :weight="2" fill-color="#fff7e6" :fill-opacity="0.95">
                  <l-popup>
                    <b>{{ e.name }}</b> · estimated, {{ Math.round(e.score * 100) }}% match<br/>
                    <span v-if="e.city"><span class="cf-k">Location:</span> {{ e.city }}<span v-if="e.county">, {{ e.county }}</span><span v-if="e.position === 'seat'"> (registered office)</span><br/></span>
                    <span class="cf-k">Activity:</span> {{ activityText(e.activity) }}<br/>
                    <span v-if="e.phone"><span class="cf-k">Phone:</span> <a :href="'tel:' + e.phone">{{ e.phone }}</a><br/></span>
                    <span v-if="e.cui" class="cf-k">CUI {{ e.cui }}</span>
                  </l-popup>
                </l-circle-marker>
              </template>
              <l-circle-marker :lat-lng="[center.lat, center.lng]" :radius="8" color="#ffffff" :weight="2" fill-color="#e03131" :fill-opacity="1">
                <l-popup>📍 Start: {{ centerCity || 'map point' }}</l-popup>
              </l-circle-marker>
            </l-map>
          </div>
        </vx-card>

        <!-- TRANSPORTED VOLUMES (supply) -->
        <vx-card :title="'Transported volumes — ' + periodLabel + ' · ' + radiusKm + ' km'" class="mb-4">
          <div class="cf-supply-head">
            <span><b>{{ supplyTotal }}</b> m³ over {{ periodLabel }}</span>
            <span>{{ supply.length }} producers within {{ radiusKm }} km</span>
          </div>
          <div v-if="loadingSupply" class="text-center my-4"><vs-progress indeterminate color="primary"></vs-progress></div>
          <div v-else-if="!supply.length" class="text-center text-muted my-4">No transports for these filters.</div>
          <vs-table v-else :data="supply" striped max-items="8" pagination search class="mt-2">
            <template slot="thead">
              <vs-th sort-key="emittant">Producer</vs-th>
              <vs-th>Species</vs-th>
              <vs-th>Products</vs-th>
              <vs-th sort-key="sumvol">Volume (m³)</vs-th>
              <vs-th sort-key="n">Transports</vs-th>
              <vs-th sort-key="dist_km">Distance</vs-th>
              <vs-th></vs-th>
            </template>
            <template slot-scope="{ data }">
              <vs-tr :key="i" v-for="(tr, i) in data" style="font-size:80%">
                <vs-td :data="tr.emittant">{{ tr.emittant }}</vs-td>
                <vs-td>{{ tr.species }}</vs-td>
                <vs-td>{{ tr.sortimente || '—' }}</vs-td>
                <vs-td :data="tr.sumvol">{{ tr.sumvol }}</vs-td>
                <vs-td :data="tr.n">{{ tr.n }}</vs-td>
                <vs-td :data="tr.dist_km">{{ tr.dist_km != null ? tr.dist_km + ' km' : '—' }}</vs-td>
                <vs-td>
                  <vs-button size="small" type="border" icon-pack="feather" icon="icon-truck" @click="openTrucks(tr)">Trucks</vs-button>
                </vs-td>
              </vs-tr>
            </template>
          </vs-table>
        </vx-card>
      </div>

      <div class="vx-col lg:w-1/3 w-full">
        <vx-card class="mb-4">
          <div class="cf-tabs">
            <span class="cf-tab" :class="{ on: rightTab === 'buyers' }" @click="rightTab = 'buyers'">Buyers <b>{{ buyers.length }}</b></span>
            <span class="cf-tab" :class="{ on: rightTab === 'transporters' }" @click="rightTab = 'transporters'">Transporters <b>{{ transporters.length }}</b></span>
            <span class="cf-tab est" :class="{ on: rightTab === 'estimated' }" @click="rightTab = 'estimated'">Estimated <b>{{ estimated.length }}</b></span>
          </div>
          <div class="cf-search-row">
            <input v-model="listSearch" class="cf-search" placeholder="Search a company, city, CUI, phone or producer…" />
            <span v-if="listSearch" class="cf-search-clear" title="Clear" @click="listSearch = ''">×</span>
            <span v-if="listSearch" class="cf-note">{{ tabRows.length }} of {{ tabTotal }}</span>
          </div>

          <!-- BUYERS -->
          <template v-if="rightTab === 'buyers'">
          <div class="cf-list-head">
            <span class="cf-note">Seen loading at a producer of the zone with their own trucks. <a href="#" @click.prevent="helpPopup = true">Why is a buyer missing?</a></span>
          </div>
          <div v-if="loadingBuyers" class="text-center my-6"><vs-progress indeterminate color="primary"></vs-progress><p class="mt-2">Loading…</p></div>
          <div v-else-if="!buyers.length" class="text-center text-muted my-6">
            No buyer seen around here yet. Buyers who never come with their own trucks don't appear here: see the Estimated tab.
          </div>
          <div v-else-if="!shownBuyers.length" class="text-center text-muted my-6">No buyer matches “{{ listSearch }}”.</div>
          <div v-else class="cf-list">
            <div v-for="b in shownBuyers" :key="b.key" class="cf-dest" @click="focus(b)">
              <div class="cf-top">
                <span class="cf-nm">{{ b.name }}</span>
                <span class="cf-badge seen">seen loading</span>
              </div>
              <div class="cf-meta">
                <span v-if="b.city">📍 {{ b.city }}<span v-if="b.county">, {{ b.county }}</span></span>
                <span v-if="b.dist != null">↔ {{ b.dist }} km</span>
                <span v-if="b.caen">CAEN {{ b.caen }}</span>
                <span v-else-if="b.status === 'seen' && !b.activity_checked">activity not checked</span>
              </div>
              <div class="cf-contact" v-if="b.phone || b.website || b.cui">
                <a v-if="b.phone" :href="'tel:' + b.phone" @click.stop>📞 {{ b.phone }}</a>
                <a v-if="b.website" :href="siteHref(b.website)" target="_blank" @click.stop>🌐 site</a>
                <span v-if="b.cui" class="cf-note">CUI {{ b.cui }}</span>
              </div>
              <div class="cf-why">
                <div v-for="p in b.producers.slice(0, 3)" :key="'p' + p.cui">🚚 Loads at {{ p.name }} — {{ p.loads }}× · {{ p.m3 }} m³ · last {{ fmtDay(p.last) }}</div>
                <div v-if="b.producers.length > 3" class="cf-note">+ {{ b.producers.length - 3 }} other producer(s)</div>
              </div>
            </div>
          </div>
          </template>

          <!-- TRANSPORTERS -->
          <template v-else-if="rightTab === 'transporters'">
          <div class="cf-list-head">
            <span class="cf-note">Transport companies seen carrying for the producers of the zone. They deliver for someone else: not buyers, but they know where the loads go.</span>
          </div>
          <div v-if="loadingBuyers" class="text-center my-6"><vs-progress indeterminate color="primary"></vs-progress><p class="mt-2">Loading…</p></div>
          <div v-else-if="!transporters.length" class="text-center text-muted my-6">No transport company seen around here.</div>
          <div v-else-if="!shownTransporters.length" class="text-center text-muted my-6">No transporter matches “{{ listSearch }}”.</div>
          <div v-else class="cf-list">
            <div v-for="t in shownTransporters" :key="t.key" class="cf-dest cf-static">
              <div class="cf-top">
                <span class="cf-nm">{{ t.name }}</span>
                <span class="cf-badge transport">transport</span>
              </div>
              <div class="cf-meta">
                <span v-if="t.city">📍 {{ t.city }}<span v-if="t.county">, {{ t.county }}</span></span>
                <span v-if="t.caen">CAEN {{ t.caen }}</span>
                <span v-else>activity not checked</span>
              </div>
              <div class="cf-contact" v-if="t.phone || t.website || t.cui">
                <a v-if="t.phone" :href="'tel:' + t.phone">📞 {{ t.phone }}</a>
                <a v-if="t.website" :href="siteHref(t.website)" target="_blank">🌐 site</a>
                <span v-if="t.cui" class="cf-note">CUI {{ t.cui }}</span>
              </div>
              <div class="cf-why">
                <div v-for="p in t.producers.slice(0, 3)" :key="'tp' + p.cui">🚚 Carries for {{ p.name }} — {{ p.loads }}× · {{ p.m3 }} m³ · last {{ fmtDay(p.last) }}</div>
                <div v-if="t.producers.length > 3" class="cf-note">+ {{ t.producers.length - 3 }} other producer(s)</div>
              </div>
            </div>
          </div>
          </template>

          <!-- ESTIMATED -->
          <template v-else>
          <div class="cf-list-head">
            <span class="cf-note">Likely buyers, estimated from the trip distance (validity of each record) and where the trucks load next, among companies whose activity uses wood. Leads to check, not real buyers. <a href="#" @click.prevent="helpPopup = true">How is this estimated?</a></span>
          </div>
          <div v-if="loadingEstimated" class="text-center my-6"><vs-progress indeterminate color="primary"></vs-progress><p class="mt-2">Loading…</p></div>
          <div v-else-if="!estimateInfo.trips_with_validity" class="text-center text-muted my-6">
            No estimate yet: it needs trips recorded since the update (validity and plates). First results after a few nights.
          </div>
          <div v-else-if="!estimateInfo.register.companies" class="text-center text-muted my-6">The company register is not imported yet.</div>
          <div v-else-if="!estimated.length" class="text-center text-muted my-6">No likely buyer for these filters.</div>
          <div v-else-if="!shownEstimated.length" class="text-center text-muted my-6">No likely buyer matches “{{ listSearch }}”.</div>
          <div v-else class="cf-list">
            <div v-for="e in shownEstimated" :key="e.key" class="cf-dest" @click="focus(e)">
              <div class="cf-top">
                <span class="cf-nm">{{ e.name }}</span>
                <span class="cf-badge estimated">{{ Math.round(e.score * 100) }}% match</span>
              </div>
              <div class="cf-meta">
                <span v-if="e.city">📍 {{ e.city }}<span v-if="e.county">, {{ e.county }}</span></span>
                <span v-if="e.dist != null">↔ {{ e.dist }} km</span>
                <span>{{ activityText(e.activity) }}</span>
                <span v-if="e.position === 'seat'" title="Registered office only: the plant may be elsewhere">seat only</span>
              </div>
              <div class="cf-contact" v-if="e.phone || e.cui">
                <a v-if="e.phone" :href="'tel:' + e.phone" @click.stop>📞 {{ e.phone }}</a>
                <span v-if="e.cui" class="cf-note">CUI {{ e.cui }}</span>
              </div>
              <div class="cf-why">
                <div v-for="p in e.producers.slice(0, 2)" :key="'ep' + p.cui">
                  🧭 From {{ p.name }}: {{ p.trips }} load(s) valid {{ p.hours }} h ≈ {{ p.band_km[0] }}–{{ p.band_km[1] }} km<span v-if="p.band_source === 'default'"> (first measure)</span> · here {{ p.dist_km }} km<span v-if="p.path_fit !== null"> · trucks' next stops fit {{ Math.round(p.path_fit * 100) }}%</span>
                </div>
              </div>
            </div>
          </div>
          </template>
        </vx-card>
      </div>
    </div>

    <!-- FILTERS POPUP -->
    <vs-popup class="cf-popup" title="Settings & filters — Customer Finder" :active.sync="filterPopup">
      <p class="cf-popup-intro">
        Real buyers only, nothing modelled. A buyer is listed when it comes to load at a producer of the zone with
        its own trucks (public transport records: the carrier is neither the producer nor a transport company).
        Buyers that only receive loads never appear in the public records (the state hides the recipient): the
        Estimated tab gives likely ones.
      </p>

      <div class="cf-fgroup">
        <label class="cf-label">Starting point</label>
        <div class="cf-inline">
          <input v-model="centerCity" @keyup.enter="geocodeCity" class="cf-input" placeholder="City (default: Teiu)" />
          <vs-button size="small" color="primary" @click="geocodeCity" :disabled="geocoding">📍 Locate</vs-button>
          <vs-button size="small" type="border" @click="resetTeiu">↺ Teiu</vs-button>
        </div>
        <div class="cf-note" style="margin-top:5px">Tip: you can also click directly on the map.</div>
      </div>

      <div class="cf-fgroup">
        <label class="cf-label">Supply collection radius</label>
        <div class="cf-chips">
          <span v-for="r in radiusPresets" :key="r" class="cf-chip" :class="{ on: radiusKm === r }" @click="radiusKm = r">{{ r }} km</span>
        </div>
        <div class="cf-inline" style="margin-top:6px">
          <input type="range" min="20" max="250" step="10" v-model.number="radiusKm" style="flex:1" />
          <span class="cf-note" style="width:60px;text-align:right">{{ radiusKm }} km</span>
        </div>
      </div>

      <div class="cf-fgroup">
        <label class="cf-label">Period (transported volumes)</label>
        <div class="cf-chips">
          <span v-for="p in periodPresets" :key="p.v" class="cf-chip" :class="{ on: monthsBack === p.v }" @click="monthsBack = p.v">{{ p.l }}</span>
        </div>
      </div>

      <div class="cf-fgroup">
        <label class="cf-label">Species (filters the supply around the start)
          <span class="cf-all" @click="toggleAll('speciesSel', speciesOptions)">{{ speciesAllOn ? 'Deselect all' : 'Select all' }}</span>
        </label>
        <div class="cf-chips">
          <span v-for="opt in speciesOptions" :key="opt.key" class="cf-chip"
            :class="{ on: speciesSel.includes(opt.key) }" @click="toggle('speciesSel', opt.key)">{{ opt.label }}</span>
        </div>
      </div>

      <div class="cf-fgroup">
        <label class="cf-label">Product (filters the producers and their buyers)
          <span class="cf-all" @click="toggleAll('productSel', productOptions)">{{ productAllOn ? 'Deselect all' : 'Select all' }}</span>
        </label>
        <div class="cf-chips">
          <span v-for="opt in productOptions" :key="opt.key" class="cf-chip prod"
            :class="{ on: productSel.includes(opt.key) }" @click="toggle('productSel', opt.key)">{{ opt.label }}</span>
        </div>
      </div>

      <div class="cf-popup-actions">
        <vs-button type="border" @click="filterPopup = false">Cancel</vs-button>
        <vs-button color="primary" @click="applyFilters">Apply</vs-button>
      </div>
    </vs-popup>

    <!-- TRUCKS POPUP — conteneur stable : vs-popup part dans <body> -->
    <div>
      <vs-popup class="cf-popup cf-trucks-popup" :title="'Trucks — ' + (trucksFor ? trucksFor.name : '')" :active.sync="trucksPopup">
        <div v-if="trucksLoading" class="text-center my-4"><vs-progress indeterminate color="primary"></vs-progress></div>
        <div v-else-if="trucksError" class="cf-note">{{ trucksError }}</div>
        <div v-else-if="trucks">
          <p class="cf-popup-intro">
            Wood chips, sawdust, bark and residues loaded by this producer over the last {{ periodLabel }}.
            The state hides who receives the load: where a truck loads next is a lead to check, not proof of the buyer.
          </p>

          <label class="cf-label">Carriers <span class="cf-note">(carrier recorded since 23 Jul 2026)</span></label>
          <div v-if="!trucks.carriers.companies.length && !trucks.carriers.private.loads" class="cf-note cf-gap">No transport recorded.</div>
          <table v-else class="cf-tbl cf-gap">
            <thead><tr><th>Carrier</th><th>Transports</th><th>m³</th><th>Last</th></tr></thead>
            <tbody>
              <tr v-for="c in trucks.carriers.companies" :key="c.cui">
                <td>{{ c.name }} <span class="cf-note">CUI {{ c.cui }}</span> <span v-if="c.is_producer" class="cf-pill">producer's own trucks</span></td>
                <td>{{ c.loads }}</td>
                <td>{{ c.m3 }}</td>
                <td>{{ fmtDay(c.last) }}</td>
              </tr>
              <tr v-if="trucks.carriers.private.loads">
                <td class="cf-note">Private persons (names not kept)</td>
                <td>{{ trucks.carriers.private.loads }}</td>
                <td>{{ trucks.carriers.private.m3 }}</td>
                <td></td>
              </tr>
            </tbody>
          </table>

          <label class="cf-label">Trucks <span class="cf-note">(plates recorded since {{ trucks.plates_since ? fmtDay(trucks.plates_since, true) : 'the next nightly run' }})</span></label>
          <div v-if="!trucks.trucks.length" class="cf-note">No plate recorded yet for this producer.</div>
          <table v-else class="cf-tbl">
            <thead><tr><th>Plate</th><th>Carrier</th><th>Transports</th><th>m³</th><th>Last</th><th>Next load elsewhere</th><th>Round trip</th></tr></thead>
            <tbody>
              <tr v-for="t in trucks.trucks" :key="t.plate">
                <td><span class="cf-plate">{{ t.plate }}</span></td>
                <td>{{ t.carrier || '—' }}</td>
                <td>{{ t.loads }}</td>
                <td>{{ t.m3 }}</td>
                <td>{{ fmtDay(t.last) }}</td>
                <td>
                  <div v-for="g in t.next.slice(0, 3)" :key="g.cui || g.name">
                    {{ g.name || 'Unknown' }} · {{ g.km != null ? g.km + ' km' : '? km' }} · +{{ g.median_h }} h <span class="cf-note">({{ g.n }}×)</span>
                  </div>
                  <span v-if="!t.next.length" class="cf-note">—</span>
                </td>
                <td>
                  <span v-if="t.round_trips">~{{ t.round_trips.median_h }} h → within ~{{ t.round_trips.radius_km }} km <span class="cf-note">({{ t.round_trips.n }}×)</span></span>
                  <span v-else-if="t.back_later" class="cf-note">back the next day ({{ t.back_later }}×)</span>
                  <span v-else class="cf-note">—</span>
                </td>
              </tr>
            </tbody>
          </table>
          <div v-if="trucks.trips_without_plate" class="cf-note cf-gap-top">+ {{ trucks.trips_without_plate }} transport(s) by private persons: no plate kept.</div>
          <div class="cf-note cf-gap-top">"Within ~x km" assumes 60 km/h and 1 h to load and unload.</div>
        </div>
      </vs-popup>
    </div>
    <!-- HOW IT WORKS — conteneur stable : vs-popup part dans <body> -->
    <div>
      <vs-popup class="cf-popup cf-help-popup" title="How Customer Finder works" :active.sync="helpPopup">
        <p class="cf-help-lead">
          Customer Finder looks for the companies that really buy wood and by-products around a start point. It reads the
          public timber transport records every night (SUMAL, the “Inspectorul Pădurii” app) and the official trade register.
        </p>

        <h5>What a transport record shows</h5>
        <div class="cf-help-fields">
          <span class="ok">✓ who loads (name, CUI)</span>
          <span class="ok">✓ where it is loaded (GPS)</span>
          <span class="ok">✓ what and how much</span>
          <span class="ok">✓ who drives the truck (company, CUI)</span>
          <span class="ok">✓ the truck plate</span>
          <span class="ok">✓ time and validity (6 h, 10 h… set by the trip distance)</span>
          <span class="no">✕ who receives the load: hidden by the state</span>
        </div>

        <h5>Two paths, two results</h5>
        <div class="cf-help-fig">
          <svg viewBox="0 0 920 260" role="img" aria-label="If the buyer comes with its own trucks, the record names it. If a transport company delivers, the receiver is hidden and only a distance can be estimated.">
            <defs>
              <marker id="cfh-ah-ok" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse"><path class="h-head-ok" d="M0,0 L10,5 L0,10 z"/></marker>
              <marker id="cfh-ah-no" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse"><path class="h-head-no" d="M0,0 L10,5 L0,10 z"/></marker>
            </defs>
            <text class="h-s" x="100" y="22" text-anchor="middle">LOADING</text>
            <text class="h-s" x="346" y="22" text-anchor="middle">THE TRUCK</text>
            <text class="h-s" x="606" y="22" text-anchor="middle">THE BUYER</text>
            <text class="h-s" x="814" y="22" text-anchor="middle">IN CUSTOMER FINDER</text>

            <rect class="h-node" x="16" y="52" width="168" height="56" rx="8"/>
            <text class="h-tb" x="100" y="76" text-anchor="middle">FBR</text>
            <text class="h-s" x="100" y="95" text-anchor="middle">Giulvăz · wood chips</text>
            <line class="h-e-ok" x1="184" y1="80" x2="512" y2="80" marker-end="url(#cfh-ah-ok)"/>
            <text class="h-t" x="346" y="70" text-anchor="middle">the buyer's own truck</text>
            <g transform="translate(250,90)"><rect x="0" y="0" width="86" height="20" rx="3" fill="#ffffff" stroke="#222222"/><rect x="0" y="0" width="13" height="20" rx="2" fill="#1f4fa8"/><text x="6.5" y="13" font-size="7" fill="#ffffff" text-anchor="middle" font-family="Arial, sans-serif">RO</text><text x="50" y="14.5" font-size="11.5" fill="#111111" text-anchor="middle" font-family="monospace" font-weight="600">TM 66 ABB</text></g>
            <g transform="translate(346,90)"><rect x="0" y="0" width="86" height="20" rx="3" fill="#ffffff" stroke="#222222"/><rect x="0" y="0" width="13" height="20" rx="2" fill="#1f4fa8"/><text x="6.5" y="13" font-size="7" fill="#ffffff" text-anchor="middle" font-family="Arial, sans-serif">RO</text><text x="50" y="14.5" font-size="11.5" fill="#111111" text-anchor="middle" font-family="monospace" font-weight="600">TM 18 ABB</text></g>
            <rect class="h-ok" x="516" y="52" width="180" height="56" rx="8"/>
            <text class="h-tb" x="606" y="76" text-anchor="middle">AGRO BADEN BANAT</text>
            <text class="h-s" x="606" y="95" text-anchor="middle">named on the record</text>
            <rect class="h-pill-ok" x="726" y="62" width="176" height="36" rx="18"/>
            <text class="h-ok-t" x="814" y="85" text-anchor="middle">✓ BUYERS · seen loading</text>

            <rect class="h-node" x="16" y="172" width="168" height="56" rx="8"/>
            <text class="h-tb" x="100" y="196" text-anchor="middle">FBR</text>
            <text class="h-s" x="100" y="215" text-anchor="middle">Giulvăz · wood chips</text>
            <line class="h-e-no" x1="184" y1="200" x2="512" y2="200" marker-end="url(#cfh-ah-no)"/>
            <text class="h-t" x="346" y="190" text-anchor="middle">a transport company's truck (VASY-TRANS)</text>
            <g transform="translate(303,210)"><rect x="0" y="0" width="86" height="20" rx="3" fill="#ffffff" stroke="#222222"/><rect x="0" y="0" width="13" height="20" rx="2" fill="#1f4fa8"/><text x="6.5" y="13" font-size="7" fill="#ffffff" text-anchor="middle" font-family="Arial, sans-serif">RO</text><text x="50" y="14.5" font-size="11.5" fill="#111111" text-anchor="middle" font-family="monospace" font-weight="600">TM 72 WAS</text></g>
            <rect class="h-hidden" x="516" y="172" width="180" height="56" rx="8"/>
            <text class="h-q" x="560" y="211" text-anchor="middle">?</text>
            <text class="h-tb" x="632" y="196" text-anchor="middle">hidden</text>
            <text class="h-s" x="632" y="215" text-anchor="middle">by the state</text>
            <rect class="h-pill-warn" x="726" y="182" width="176" height="36" rx="18"/>
            <text class="h-warn-t" x="814" y="205" text-anchor="middle">≈ ESTIMATED · distance</text>
            <text class="h-s" x="814" y="244" text-anchor="middle">10 h ≈ 35-40 km, no name</text>
          </svg>
        </div>
        <p class="cf-help-cap">What decides everything is who drives the truck. Green: the buyer is known for sure. Red dashes: the record stops at the transport company and the receiver appears nowhere, so only a distance can be estimated.</p>

        <h5>The distance clue: the 10-hour ring (example: FBR)</h5>
        <div class="cf-help-fig">
          <svg viewBox="0 0 700 560" role="img" aria-label="Around FBR's depot in Giulvăz, the buyer of the 10-hour records is in a ring from 20 to 60 km. Jimbolia (35 km) and Pelletino's registered office in Ianova (46 km) are in it; the offices of Luu Oak (Sebiș, 126 km) and Corden (Podari, 263 km) are much further.">
            <defs>
              <marker id="cfh-ah-far" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto-start-reverse"><path class="h-head-no" d="M0,0 L10,5 L0,10 z"/></marker>
            </defs>
            <circle class="h-band" cx="320" cy="290" r="200"/>
            <circle class="h-hole" cx="320" cy="290" r="66.7"/>
            <circle class="h-ring" cx="320" cy="290" r="200"/>
            <circle class="h-ring" cx="320" cy="290" r="66.7"/>
            <circle class="h-ring-m" cx="320" cy="290" r="123.3"/>
            <text class="h-s" x="320" y="82" text-anchor="middle">60 km</text>
            <text class="h-s" x="320" y="215" text-anchor="middle">20 km</text>
            <text class="h-acc" x="208" y="393" text-anchor="middle">~37 km measured (10 h)</text>
            <text class="h-s" x="320" y="520" text-anchor="middle">Where the buyer of the 10-hour records is</text>
            <rect class="h-dot-acc" x="313" y="283" width="14" height="14" rx="2"/>
            <text class="h-tb" x="320" y="318" text-anchor="middle">FBR</text>
            <text class="h-s" x="320" y="334" text-anchor="middle">Giulvăz</text>
            <circle class="h-dot" cx="247" cy="200" r="5"/>
            <text class="h-t" x="239" y="196" text-anchor="end">Jimbolia · 35 km</text>
            <text class="h-s" x="239" y="212" text-anchor="end">Pelletino's plant?</text>
            <circle class="h-dot" cx="380" cy="214" r="5"/>
            <text class="h-t" x="390" y="226">Timișoara · 29 km</text>
            <circle class="h-dot" cx="430" cy="182" r="5"/>
            <text class="h-t" x="440" y="186">Ianova · 46 km</text>
            <text class="h-s" x="440" y="202">Pelletino PTM's office</text>
            <line class="h-e-no" x1="465" y1="138" x2="508" y2="93" marker-end="url(#cfh-ah-far)"/>
            <text class="h-t" x="514" y="80">Sebiș · 126 km</text>
            <text class="h-s" x="514" y="96">Luu Oak's office</text>
            <line class="h-e-no" x1="496" y1="405" x2="540" y2="434" marker-end="url(#cfh-ah-far)"/>
            <text class="h-t" x="548" y="440">Podari (Dolj) · 263 km</text>
            <text class="h-s" x="548" y="456">Corden Biochem's office</text>
          </svg>
        </div>
        <p class="cf-help-cap">
          On our own trips, 9 to 15 km give 6 h and about 37 km gives 10 h. FBR's records carried by VASY-TRANS are valid 10 h,
          so the buyer is somewhere in the ring. The trade register only gives registered offices: a buyer whose office is far
          from its plant falls outside the ring and the estimate looks in the wrong place.
        </p>

        <h5>Real test: FBR's trucks on 16 Oct 2026</h5>
        <div class="cf-help-table">
          <table class="cf-tbl">
            <thead><tr><th>Time</th><th>Truck</th><th>Driven by</th><th>Validity</th><th>What the system gets</th></tr></thead>
            <tbody>
              <tr><td>09:00</td><td><span class="cf-plate">TM 72 WAS</span></td><td>VASY-TRANS · road haulage (4941)</td><td>10 h</td><td>≈ estimated: buyer ~35-40 km away, no name</td></tr>
              <tr><td>13:41</td><td><span class="cf-plate">TM 66 ABB</span></td><td>AGRO BADEN BANAT · grain trade (4621)</td><td>6 h</td><td>✓ named: AGRO BADEN BANAT, ~20 km or less</td></tr>
              <tr><td>14:10</td><td><span class="cf-plate">TM 18 ABB</span></td><td>AGRO BADEN BANAT</td><td>6 h</td><td>✓ named: same buyer</td></tr>
              <tr><td>14:52</td><td><span class="cf-plate">TM 72 WAS</span></td><td>VASY-TRANS</td><td>10 h</td><td>≈ estimated: 2nd trip, 5 h 52 after the 1st, came back empty</td></tr>
            </tbody>
          </table>
        </div>
        <p>
          We followed the plates: all 635 records issued within 60 km of FBR between 16 Oct 9:00 and 17 Oct 18:00. The three
          trucks only appear at FBR: after delivering, they load nothing else in that zone and time, so the plate leads to no
          customer in the public records. AGRO BADEN BANAT comes with its own trucks: almost certainly a buyer. Florent's three
          customers are all in the trade register, but none shows up as a buyer of FBR: Pelletino PTM ranks 171st of 369
          estimates, Corden and Luu Oak are out of reach (offices far away).
        </p>

        <h5>When it works, when it doesn't</h5>
        <div class="cf-help-verdict">
          <div class="v ok">
            <b>✓ Works</b>
            <ul>
              <li>The buyer comes to load with its own trucks: the record names it (AGRO BADEN BANAT at FBR, Luu Oak at Tuberoza).</li>
              <li>Transport companies are all known: Transporters tab, worth a call.</li>
            </ul>
          </div>
          <div class="v warn">
            <b>≈ Helps, no certainty</b>
            <ul>
              <li>A transport company delivers: the record's validity gives the distance, a ring around the producer.</li>
              <li>The buyer's office is near its plant: the estimate puts it in the ring, among others.</li>
              <li>The truck loads elsewhere right after: that gives the direction. Plates are recorded every night from now on.</li>
            </ul>
          </div>
          <div class="v no">
            <b>✕ Doesn't work</b>
            <ul>
              <li>A transport company delivers and drives back empty: the receiver appears nowhere (FBR with VASY-TRANS).</li>
              <li>The office is far from the plant: the estimate looks in the wrong place (Corden, Luu Oak).</li>
              <li>The buyer only receives and never ships wood: no record carries its name.</li>
            </ul>
          </div>
        </div>

        <h5>The tabs</h5>
        <ul>
          <li><b>Buyers</b>: seen loading with their own trucks. Nothing estimated.</li>
          <li><b>Transporters</b>: transport companies carrying for the producers of the zone. Not buyers, but they know where
            the loads go. Private persons are never listed.</li>
          <li><b>Estimated</b>: likely buyers from three clues: the distance (record validity, measured every night on trips whose
            arrival we know), the direction (where the same truck loads next, within 12 h) and who is there (trade register
            companies whose activity uses wood, checked with ANAF). “Seat only” means registered office: the plant may be
            elsewhere.</li>
          <li><b>Trucks</b> (button on each producer): carriers, plates, next loads and round trips.</li>
        </ul>

        <h5>Filters and data</h5>
        <p>
          The zone (start point and radius), period, species and product apply to producers, buyers and transporters.
          Carriers are known since 23 Jul 2026; plates and validity from the first night after the 18 Oct 2026 update;
          trade register of 2 Sep 2026.
        </p>
      </vs-popup>
    </div>
  </div>
</template>

<script>
import axios from '@/axios.js';
import { LMap, LTileLayer, LCircle, LCircleMarker, LPopup, LPolyline } from 'vue2-leaflet';
import 'leaflet/dist/leaflet.css';

const TEIU = { lat: 45.8953420, lng: 22.5019217 };
const GEO_COUNTRIES = 'ro,hu,rs,bg';

// Sans accents ni casse : « Timişoara » = « timisoara ».
function fold(s) {
  return String(s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

// Fond de carte : OpenFreeMap (open source, sans clé ni quota), style Positron = l'ancien CARTO light_all,
// qui renvoie une tuile « API KEY REQUIRED » depuis 2026. MapLibre est chargé à la demande depuis le CDN
// (webpack 4 n'empaquette pas maplibre-gl 4) ; sans WebGL ou si le CDN/le style échoue → tuiles raster OSM.
const BASEMAP_STYLE = 'https://tiles.openfreemap.org/styles/positron';
const MAPLIBRE_JS = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js';
const MAPLIBRE_CSS = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css';
const MAPLIBRE_LEAFLET_JS = 'https://unpkg.com/@maplibre/maplibre-gl-leaflet@0.0.22/leaflet-maplibre-gl.js';
let maplibreReady = null;

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error('Failed to load ' + src));
    document.head.appendChild(s);
  });
}

function loadMaplibre() {
  if (!maplibreReady) {
    if (!document.querySelector('link[href="' + MAPLIBRE_CSS + '"]')) {
      const l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = MAPLIBRE_CSS;
      document.head.appendChild(l);
    }
    maplibreReady = (window.maplibregl ? Promise.resolve() : loadScript(MAPLIBRE_JS))
      .then(() => (window.L && window.L.maplibreGL ? null : loadScript(MAPLIBRE_LEAFLET_JS)))
      .catch((e) => { maplibreReady = null; throw e; });
  }
  return maplibreReady;
}

export default {
  components: { LMap, LTileLayer, LCircle, LCircleMarker, LPopup, LPolyline },
  data() {
    return {
      teiu: { ...TEIU },
      center: { ...TEIU },
      centerCity: 'Teiu',
      geocoding: false,
      radiusKm: 50,
      monthsBack: 3,
      radiusPresets: [50, 80, 100, 150, 200],
      periodPresets: [{ v: 3, l: '3 mo' }, { v: 6, l: '6 mo' }, { v: 12, l: '1 year' }, { v: 24, l: '2 years' }],
      filterPopup: false,
      speciesOptions: [
        { key: 'salcam',    label: 'Black locust', pat: ['%SALCÂM%', '%SALCAM%'] },
        { key: 'stejar',    label: 'Oak',          pat: ['%STEJAR%', '%GORUN%', '%CER (%'] },
        { key: 'castan',    label: 'Chestnut',     pat: ['%CASTAN BUN%'] },
        { key: 'fag',       label: 'Beech',        pat: ['%FAG%'] },
        { key: 'frasin',    label: 'Ash',          pat: ['%FRASIN%'] },
        { key: 'rasinoase', label: 'Softwood',     pat: ['%MOLID%', '%BRAD%', '%PIN %', '%RĂȘINOASE%'] },
      ],
      productOptions: [
        { key: 'rotund',    label: 'Logs',      pat: ['%Lemn rotund%'] },
        { key: 'cherestea', label: 'Sawn wood', pat: ['%Cherestea%', '%Cherestele%'] },
        { key: 'foc',       label: 'Firewood',  pat: ['%Lemn de foc%'] },
        { key: 'dechets',   label: 'Waste/sawdust', pat: ['%Rumeguș%', '%Tocătur%', '%Resturi%', '%Coaj%', '%Lături%'] },
      ],
      speciesSel: [],
      productSel: ['dechets'],
      supply: [],
      loadingSupply: false,
      buyers: [],
      transporters: [],
      estimated: [],
      estimateInfo: { trips_with_validity: 0, register: { companies: 0 } },
      loadingEstimated: false,
      estimatedReq: 0,
      rightTab: 'buyers',
      listSearch: '',
      helpPopup: false,
      loadingBuyers: false,
      buyersReq: 0,
      mapZoom: 8,
      mapCenter: [TEIU.lat, TEIU.lng],
      useRaster: false,
      tileUrl: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      tileAttr: '© <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors',
      supplyTotal: 0,
      trucksPopup: false,
      trucksFor: null,
      trucks: null,
      trucksLoading: false,
      trucksError: '',
      trucksReq: 0,
    };
  },
  created() {
    const roles = localStorage.getItem('login-roles');
    if (!roles || !(roles.toLowerCase().includes('admin') || roles.toLowerCase().includes('moderator') || roles.toLowerCase().includes('wood finder'))) {
      this.$router.push({ path: '/dashboard/analytics' });
      return;
    }
    this.runSupply();
  },
  mounted() {
    this.invalidate(500);
    this.addBasemap();
  },
  computed: {
    periodLabel() {
      const p = this.periodPresets.find(x => x.v === this.monthsBack);
      return p ? p.l : this.monthsBack + ' mo';
    },
    speciesAllOn() {
      return this.speciesSel.length === this.speciesOptions.length;
    },
    productAllOn() {
      return this.productSel.length === this.productOptions.length;
    },
    speciesSummary() {
      if (!this.speciesSel.length) return 'all species';
      if (this.speciesAllOn) return 'all listed species';
      return this.speciesSel.map(k => (this.speciesOptions.find(o => o.key === k) || {}).label).join(', ');
    },
    productSummary() {
      if (!this.productSel.length) return 'all products';
      if (this.productAllOn) return 'all listed products';
      return this.productSel.map(k => (this.productOptions.find(o => o.key === k) || {}).label).join(', ');
    },
    seenCount() {
      return this.buyers.filter(b => b.loads > 0).length;
    },
    // Recherche des onglets : nom, ville, département, CUI, téléphone, producteurs, activité (sans accents).
    searchTerms() {
      return fold(this.listSearch).split(' ').filter(Boolean);
    },
    shownBuyers() {
      return this.searchFilter(this.buyers);
    },
    shownTransporters() {
      return this.searchFilter(this.transporters);
    },
    shownEstimated() {
      return this.searchFilter(this.estimated);
    },
    tabTotal() {
      if (this.rightTab === 'estimated') return this.estimated.length;
      return this.rightTab === 'buyers' ? this.buyers.length : this.transporters.length;
    },
    mappableBuyers() {
      return this.shownBuyers.filter(b => b.lat != null && b.lng != null);
    },
    // Producteur → acheteur vu en train de charger.
    buyerLines() {
      const lines = [];
      for (const b of this.mappableBuyers) {
        for (const p of b.producers) {
          if (p.lat == null || p.lng == null) continue;
          lines.push({ key: 's' + b.key + '-' + p.cui, from: [p.lat, p.lng], to: [b.lat, b.lng], color: '#3b82f6', weight: 1 + Math.min(5, p.loads / 5) });
        }
      }
      return lines;
    },
    supplyMarkers() {
      return this.supply.slice(0, 300);
    },
    tabRows() {
      if (this.rightTab === 'estimated') return this.shownEstimated;
      return this.rightTab === 'buyers' ? this.shownBuyers : this.shownTransporters;
    },
    mappableEstimated() {
      return this.shownEstimated.filter(e => e.lat != null && e.lng != null);
    },
    // Producteur → acheteur estimé (pointillés orange).
    estimatedLines() {
      const lines = [];
      for (const e of this.mappableEstimated) {
        for (const p of e.producers.slice(0, 2)) {
          if (p.lat == null || p.lng == null) continue;
          lines.push({ key: e.key + '-' + p.cui, from: [p.lat, p.lng], to: [e.lat, e.lng] });
        }
      }
      return lines;
    },
  },
  methods: {
    async addBasemap() {
      try {
        await loadMaplibre();
        const map = this.$refs.cfmap && this.$refs.cfmap.mapObject;
        if (!map) return;
        const layer = window.L.maplibreGL({
          style: BASEMAP_STYLE,
          attribution: '<a href="https://openfreemap.org" target="_blank">OpenFreeMap</a> © <a href="https://www.openmaptiles.org/" target="_blank">OpenMapTiles</a> © <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors',
        }).addTo(map);
        const gl = layer.getMaplibreMap();
        let loaded = false;
        gl.once('load', () => { loaded = true; });
        gl.on('error', () => {
          if (loaded || this.useRaster) return;
          map.removeLayer(layer);
          this.useRaster = true;
        });
      } catch (e) {
        this.useRaster = true;
      }
    },
    invalidate(delay) {
      this.$nextTick(() => setTimeout(() => { try { if (this.$refs.cfmap && this.$refs.cfmap.mapObject) this.$refs.cfmap.mapObject.invalidateSize(); } catch (e) { /* noop */ } }, delay || 300));
    },
    siteHref(w) { return /^https?:/i.test(w) ? w : ('https://' + w); },
    toggle(field, key) {
      const arr = this[field], i = arr.indexOf(key);
      if (i === -1) arr.push(key); else arr.splice(i, 1);
    },
    // Tout sélectionner / tout désélectionner si déjà complet.
    toggleAll(field, options) {
      this[field] = this[field].length === options.length ? [] : options.map(o => o.key);
    },
    openFilters() { this.filterPopup = true; },
    applyFilters() { this.filterPopup = false; this.runSupply(); this.invalidate(300); },
    resetTeiu() { this.center = { ...this.teiu }; this.centerCity = 'Teiu'; this.mapCenter = [this.teiu.lat, this.teiu.lng]; this.mapZoom = 8; },
    onMapClick(e) {
      if (!e || !e.latlng) return;
      this.center = { lat: e.latlng.lat, lng: e.latlng.lng };
      this.centerCity = '';
      this.runSupply();
    },
    async geocodeCity() {
      const q = (this.centerCity || '').trim();
      if (!q) return;
      this.geocoding = true;
      try {
        // Le point de départ reste roumain dans 99 % des cas ; pays voisins autorisés pour un hub frontalier.
        const r = await fetch('https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=' + GEO_COUNTRIES + '&q=' + encodeURIComponent(q));
        const data = await r.json();
        if (!data || !data.length) { this.$vs.notify({ title: 'Not found', text: q, color: 'warning', position: 'bottom-right' }); return; }
        this.center = { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
        this.mapCenter = [this.center.lat, this.center.lng];
        this.mapZoom = 9;
      } catch (e) {
        this.$vs.notify({ title: 'Error', text: 'Geocoding failed.', color: 'danger', position: 'bottom-right' });
      } finally { this.geocoding = false; }
    },
    buildPatterns(sel, options) {
      const pats = [];
      for (const key of sel) { const opt = options.find(o => o.key === key); if (opt) pats.push(...opt.pat); }
      return pats;
    },
    // Acheteurs réels de la zone (mêmes filtres que les volumes) : vus en train de charger + confirmés.
    async loadBuyers() {
      const req = ++this.buyersReq;
      this.loadingBuyers = true;
      try {
        const r = await axios.get('inspaduri/buyers', {
          params: {
            lat: this.center.lat, lng: this.center.lng, radiusKm: this.radiusKm, months: this.monthsBack,
            species: this.buildPatterns(this.speciesSel, this.speciesOptions),
            sortimente: this.buildPatterns(this.productSel, this.productOptions),
          },
        });
        if (req !== this.buyersReq) return;
        this.buyers = (r.data && r.data.buyers) || [];
        this.transporters = (r.data && r.data.transporters) || [];
      } catch (e) {
        if (req !== this.buyersReq) return;
        this.buyers = []; this.transporters = [];
        this.$vs.notify({ title: 'Error', text: 'Failed to load the buyers.', color: 'danger', position: 'bottom-right' });
      } finally {
        if (req === this.buyersReq) this.loadingBuyers = false;
      }
    },
    // Acheteurs estimés de la zone (distance de l'aviz + chargement suivant + registre ONRC).
    async loadEstimated() {
      const req = ++this.estimatedReq;
      this.loadingEstimated = true;
      try {
        const r = await axios.get('inspaduri/estimated', {
          params: {
            lat: this.center.lat, lng: this.center.lng, radiusKm: this.radiusKm, months: this.monthsBack,
            species: this.buildPatterns(this.speciesSel, this.speciesOptions),
            sortimente: this.buildPatterns(this.productSel, this.productOptions),
          },
        });
        if (req !== this.estimatedReq) return;
        this.estimated = (r.data && r.data.buyers) || [];
        this.estimateInfo = r.data || { trips_with_validity: 0, register: { companies: 0 } };
      } catch (e) {
        if (req !== this.estimatedReq) return;
        this.estimated = [];
        this.estimateInfo = { trips_with_validity: 0, register: { companies: 0 } };
      } finally {
        if (req === this.estimatedReq) this.loadingEstimated = false;
      }
    },
    searchFilter(list) {
      const terms = this.searchTerms;
      if (!terms.length) return list;
      return list.filter(x => {
        const hay = fold([
          x.name, x.city, x.county, x.cui, x.phone, x.caen, x.status,
          x.activity && x.activity.label,
          ...(x.producers || []).map(p => p.name),
        ].filter(Boolean).join(' '));
        return terms.every(t => hay.indexOf(t) !== -1);
      });
    },
    activityText(a) {
      if (!a) return '';
      return (a.code ? 'CAEN ' + a.code + ' · ' : '') + a.label + (a.other_main ? ' (main activity ' + a.other_main + ')' : '');
    },
    async runSupply() {
      this.loadBuyers();
      this.loadEstimated();
      this.loadingSupply = true;
      try {
        const d = new Date(); d.setMonth(d.getMonth() - this.monthsBack);
        const body = {
          species: this.buildPatterns(this.speciesSel, this.speciesOptions),
          sortimente: this.buildPatterns(this.productSel, this.productOptions),
          centerLat: this.center.lat, centerLng: this.center.lng, radiusKm: this.radiusKm,
          datestart: d.toISOString().slice(0, 10),
        };
        const r = await axios.post('inspaduri/search', body);
        this.supply = (r.data && r.data.rows) || [];
        this.supplyTotal = this.supply.reduce((s, x) => s + (x.sumvol || 0), 0).toLocaleString('en-US');
      } catch (e) {
        this.supply = []; this.supplyTotal = 0;
      } finally { this.loadingSupply = false; }
    },
    // Camions d'un producteur : transporteurs (depuis juillet) + plaques et chargement suivant.
    async openTrucks(s) {
      const req = ++this.trucksReq;
      this.trucksFor = { name: s.emittant };
      this.trucks = null;
      this.trucksError = '';
      this.trucksPopup = true;
      this.trucksLoading = true;
      try {
        const r = await axios.get('inspaduri/trucks', { params: { emittant: s.emittant, lat: s.lat, lng: s.lng, months: this.monthsBack } });
        if (req === this.trucksReq) this.trucks = r.data;
      } catch (e) {
        if (req === this.trucksReq) this.trucksError = (e.response && e.response.data && e.response.data.message) || 'Failed to load the trucks.';
      } finally {
        if (req === this.trucksReq) this.trucksLoading = false;
      }
    },
    fmtDay(d, withYear) {
      if (!d) return '—';
      const x = new Date(d);
      if (isNaN(x.getTime())) return '—';
      return x.toLocaleDateString('en-GB', withYear ? { day: 'numeric', month: 'short', year: 'numeric' } : { day: 'numeric', month: 'short' });
    },
    focus(c) {
      if (c.lat == null) return;
      this.mapCenter = [c.lat, c.lng];
      this.mapZoom = 10;
    },
    // Export de l'onglet affiché : acheteurs, transporteurs ou estimés.
    exportCSV() {
      if (this.rightTab === 'estimated') return this.exportEstimatedCSV();
      const buyersTab = this.rightTab === 'buyers';
      const cols = buyersTab
        ? ['company', 'cui', 'caen', 'city', 'county', 'distance_km', 'phone', 'website', 'loads_seen', 'm3_seen', 'last_load', 'loads_at']
        : ['company', 'cui', 'caen', 'city', 'county', 'phone', 'website', 'loads', 'm3', 'last_load', 'carries_for'];
      const rows = buyersTab
        ? this.shownBuyers.map(b => [
          b.name, b.cui, b.caen, b.city, b.county, b.dist, b.phone, b.website, b.loads, b.m3,
          b.last ? String(b.last).slice(0, 10) : '',
          b.producers.map(p => p.name + ' (' + p.loads + ')').join(', '),
        ])
        : this.shownTransporters.map(t => [
          t.name, t.cui, t.caen, t.city, t.county, t.phone, t.website, t.loads, t.m3,
          t.last ? String(t.last).slice(0, 10) : '',
          t.producers.map(p => p.name + ' (' + p.loads + ')').join(', '),
        ]);
      const q = v => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"';
      const csv = '﻿' + [cols.map(q).join(';')].concat(rows.map(r => r.map(q).join(';'))).join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = (buyersTab ? 'real_buyers_' : 'transporters_') + (this.centerCity || 'zone').toLowerCase().replace(/[^a-z0-9]+/g, '-') + '.csv';
      document.body.appendChild(a); a.click(); a.remove();
    },
    exportEstimatedCSV() {
      const cols = ['company', 'cui', 'city', 'county', 'position', 'distance_km', 'activity', 'match_%', 'phone', 'likely_from'];
      const rows = this.shownEstimated.map(e => [
        e.name, e.cui, e.city, e.county, e.position, e.dist, this.activityText(e.activity), Math.round(e.score * 100), e.phone,
        e.producers.map(p => p.name + ' (' + p.dist_km + ' km, ' + p.hours + ' h)').join(', '),
      ]);
      const q = v => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"';
      const csv = '\ufeff' + [cols.map(q).join(';')].concat(rows.map(r => r.map(q).join(';'))).join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'estimated_buyers_' + (this.centerCity || 'zone').toLowerCase().replace(/[^a-z0-9]+/g, '-') + '.csv';
      document.body.appendChild(a); a.click(); a.remove();
    },
  },
};
</script>

<style lang="scss">
#customer-finder {
  .cf-toolbar { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
  .cf-title h4 { margin: 0; font-weight: 700; }
  .cf-title .cf-sub { font-size: 12px; color: #7b8794; }
  .cf-summary { display: flex; gap: 8px; flex-wrap: wrap; flex: 1; }
  .cf-tag { background: #f0f2f5; border: 1px solid #e2e6ea; border-radius: 20px; padding: 4px 12px; font-size: 12.5px; color: #445; }
  .cf-tag.prod { background: #fff2e8; border-color: #ffd8bf; color: #ad4e00; }
  .cf-toolbar-actions { display: flex; gap: 8px; }
  .cf-stats { display: flex; gap: 10px; margin-top: 14px; flex-wrap: wrap; }
  .cf-stat { background: #f7f8fa; border: 1px solid #e2e6ea; border-radius: 10px; padding: 8px 16px; min-width: 96px; }
  .cf-stat .n { font-size: 19px; font-weight: 800; color: #2c3e50; }
  .cf-stat .l { font-size: 10.5px; color: #7b8794; text-transform: uppercase; letter-spacing: .3px; }

  .cf-map-wrap { position: relative; height: 660px; border-radius: 8px; overflow: hidden; border: 1px solid #e2e6ea; }
  .cf-hint { position: absolute; top: 10px; left: 50px; z-index: 500; background: rgba(255,255,255,.9); border: 1px solid #e2e6ea; border-radius: 20px; padding: 4px 12px; font-size: 12px; color: #556; pointer-events: none; }
  .cf-overlay { position: absolute; inset: 0; z-index: 600; background: rgba(255,255,255,.55); display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; font-size: 13px; color: #556; }

  .cf-list { max-height: 660px; overflow-y: auto; padding-right: 4px; }
  .cf-dest { border: 1px solid #e2e6ea; border-radius: 11px; padding: 10px 11px; margin-bottom: 9px; cursor: pointer; transition: border-color .15s, box-shadow .15s; }
  .cf-dest:hover { border-color: #3b82f6; box-shadow: 0 2px 8px rgba(59,130,246,.12); }
  .cf-top { display: flex; align-items: center; gap: 8px; }
  .cf-nm { font-size: 13.5px; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .cf-badge { font-size: 9.5px; font-weight: 700; text-transform: uppercase; padding: 2px 6px; border-radius: 20px; flex: none; margin-left: auto; }
  .cf-badge.seen { background: rgba(59,130,246,.14); color: #2563eb; }
  .cf-badge.transport { background: rgba(139,152,165,.18); color: #5b6875; }
  .cf-tabs { display: flex; align-items: center; gap: 6px; margin-bottom: 12px; }
  .cf-tab { padding: 6px 14px; border-radius: 20px; font-size: 12.5px; font-weight: 600; text-transform: uppercase; letter-spacing: .3px; cursor: pointer; border: 1px solid #d8dee4; color: #556; user-select: none; }
  .cf-tab b { margin-left: 4px; font-weight: 800; }
  .cf-tab.on { background: #7367f0; border-color: #7367f0; color: #fff; }
  .cf-tab.est.on { background: #f59f00; border-color: #f59f00; }
  .cf-search-row { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
  .cf-search { flex: 1; padding: 7px 10px; border: 1px solid #d8dee4; border-radius: 6px; font-size: 13px; min-width: 0; }
  .cf-search:focus { outline: none; border-color: #7367f0; }
  .cf-search-clear { cursor: pointer; font-size: 18px; line-height: 1; color: #9aa5b1; padding: 0 2px; user-select: none; }
  .cf-search-clear:hover { color: #e03131; }
  .cf-badge.estimated { background: rgba(245,159,0,.16); color: #b26b00; }
  .cf-help { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; border: 1px solid #7367f0; color: #7367f0; font-size: 13px; font-weight: 800; cursor: pointer; vertical-align: middle; user-select: none; }
  .cf-help:hover { background: #7367f0; color: #fff; }
  .cf-static { cursor: default; }
  .cf-meta { font-size: 11.5px; color: #7b8794; margin-top: 5px; display: flex; flex-wrap: wrap; gap: 4px 10px; }
  .cf-contact { font-size: 12px; margin-top: 6px; display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
  .cf-contact a { color: #2f9e44; text-decoration: none; }
  .cf-note { font-size: 11.5px; color: #9aa5b1; }
  .cf-why { font-size: 12px; color: #556; margin-top: 6px; line-height: 1.45; }
  .cf-k { color: #888; }
  .cf-list-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 10px; }

  .cf-supply-head { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 8px; font-size: 12.5px; color: #556; margin-bottom: 6px; }
  .cf-supply-head b { color: #e8590c; font-size: 15px; }
}

/* vs-popup teleports its content to the end of <body>, outside #customer-finder,
   so these popup styles must live at the global scope (targeted via .cf-popup). */
.cf-popup {
  .cf-popup-intro { font-size: 12.5px; color: #667; line-height: 1.5; margin-bottom: 12px; }
  .cf-fgroup { margin-bottom: 16px; }
  .cf-label { display: block; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: .4px; color: #7b8794; margin-bottom: 7px; }
  .cf-chips { display: flex; flex-wrap: wrap; gap: 6px; }
  .cf-all {
    float: right; font-size: 11px; font-weight: 600; text-transform: none; letter-spacing: 0;
    color: #7367f0; cursor: pointer; user-select: none;
  }
  .cf-all:hover { text-decoration: underline; }
  .cf-chip { display: inline-block; padding: 5px 11px; border-radius: 20px; font-size: 12.5px; cursor: pointer; border: 1px solid #d8dee4; background: #fff; color: #445; user-select: none; transition: all .12s; }
  .cf-chip:hover { border-color: #7367f0; }
  .cf-chip.on { background: #7367f0; border-color: #7367f0; color: #fff; }
  .cf-chip.prod.on { background: #e8590c; border-color: #e8590c; }
  .cf-inline { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .cf-input { padding: 7px 10px; border: 1px solid #d8dee4; border-radius: 6px; font-size: 13px; min-width: 220px; }
  .cf-note { font-size: 11.5px; color: #9aa5b1; }
  .cf-popup-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 8px; }
  .cf-tbl { width: 100%; border-collapse: collapse; font-size: 12.5px; }
  .cf-tbl th { text-align: left; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .3px; color: #7b8794; padding: 6px 8px; border-bottom: 1px solid #e2e6ea; }
  .cf-tbl td { padding: 6px 8px; border-bottom: 1px solid #f0f2f5; vertical-align: top; }
  .cf-plate { font-family: monospace; font-weight: 700; background: #f7f8fa; border: 1px solid #d8dee4; border-radius: 4px; padding: 1px 6px; white-space: nowrap; }
  .cf-pill { font-size: 9.5px; font-weight: 700; text-transform: uppercase; padding: 1px 6px; border-radius: 20px; background: rgba(47,158,68,.14); color: #2f9e44; white-space: nowrap; }
  .cf-gap { margin-bottom: 18px; }
  .cf-gap-top { margin-top: 8px; }
}

.cf-help-popup {
  h5 { font-size: 14px; font-weight: 700; margin: 18px 0 8px; }
  p, li { font-size: 13px; color: #445; line-height: 1.55; }
  ul { padding-left: 18px; margin: 4px 0 8px; list-style: disc; }
  li { margin-bottom: 4px; }
  .cf-help-lead { font-size: 14px; color: #556; }
  .cf-help-cap { font-size: 12.5px; color: #7b8794; margin-top: 6px; }
  .cf-help-fields { display: flex; flex-wrap: wrap; gap: 6px; }
  .cf-help-fields span { font-size: 12.5px; padding: 3px 10px; border-radius: 20px; }
  .cf-help-fields .ok { background: rgba(47,158,68,.12); color: #2a7a3c; }
  .cf-help-fields .no { background: rgba(224,49,49,.12); color: #c92a2a; font-weight: 700; }
  .cf-help-fig { overflow-x: auto; border: 1px solid #e2e6ea; border-radius: 8px; padding: 10px; background: #fff; }
  .cf-help-fig svg { display: block; width: 100%; min-width: 620px; height: auto; }
  .cf-help-table { overflow-x: auto; }
  .cf-help-table .cf-tbl { min-width: 620px; }
  .cf-help-verdict { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; }
  .cf-help-verdict .v { border: 1px solid #e2e6ea; border-top: 4px solid var(--c); border-radius: 8px; padding: 10px 12px; }
  .cf-help-verdict .v b { color: var(--c); font-size: 13.5px; }
  .cf-help-verdict .ok { --c: #2f9e44; }
  .cf-help-verdict .warn { --c: #e8590c; }
  .cf-help-verdict .no { --c: #e03131; }
  /* schémas */
  .h-t { fill: #2c3e50; font-size: 13px; }
  .h-tb { fill: #2c3e50; font-size: 14px; font-weight: 700; }
  .h-s { fill: #7b8794; font-size: 12px; }
  .h-acc { fill: #e8590c; font-size: 12px; font-weight: 600; }
  .h-node { fill: #fff; stroke: #c9d3ce; stroke-width: 1.5; }
  .h-ok { fill: #e3f2e7; stroke: #2f9e44; stroke-width: 1.5; }
  .h-hidden { fill: #fff; stroke: #e03131; stroke-width: 1.5; stroke-dasharray: 6 5; }
  .h-pill-ok { fill: #e3f2e7; stroke: #2f9e44; }
  .h-pill-warn { fill: #fff4e6; stroke: #e8590c; }
  .h-ok-t { fill: #2a7a3c; font-size: 13px; font-weight: 700; }
  .h-warn-t { fill: #b4500a; font-size: 13px; font-weight: 700; }
  .h-q { fill: #e03131; font-size: 30px; font-weight: 700; }
  .h-e-ok { stroke: #2f9e44; stroke-width: 2; fill: none; }
  .h-e-no { stroke: #e03131; stroke-width: 2; fill: none; stroke-dasharray: 7 6; }
  .h-head-ok { fill: #2f9e44; }
  .h-head-no { fill: #e03131; }
  .h-band { fill: #eaf6ef; }
  .h-hole { fill: #fff; }
  .h-ring { fill: none; stroke: #c9d3ce; stroke-width: 1.2; }
  .h-ring-m { fill: none; stroke: #e8590c; stroke-width: 1.6; stroke-dasharray: 5 5; }
  .h-dot { fill: #2c3e50; }
  .h-dot-acc { fill: #7367f0; }
}

/* The help carries two diagrams and a table: give it more width. */
.cf-help-popup .vs-popup {
  width: 92vw;
  max-width: 980px;
}

/* The trucks popup carries two dense tables: give it more width. */
.cf-trucks-popup .vs-popup {
  width: 92vw;
  max-width: 1100px;
}
</style>
